import type { FC } from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IdTaskDto } from '@/services/ws-funnel/src/main/resources/rest-api';
import { useResolver } from '@/js/libs/form';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useModalState } from '@/js/libs/hooks/useModalState';
import { Button, Container, DropdownList, Icons, Table } from '@/js/libs/ui/src/V2';

import TableHeader from 'src/components/TableHeaderV2';
import { OrderDirection, useSortAndOrderState } from 'src/hooks/useSortAndOrderState';
import type { SelectAllProps } from 'src/types';
import { useTasks } from '../../../hooks';
import type { ColumnName } from '../../../types';
import { TaskTableStatus } from '../../../types';
import { TableFilters } from '../../TableFilters';
import { TableTabs } from '../../TableTabs';
import { TaskRow } from '../../TaskRow';
import type { ArchiveModalFormValues, AssignModalFormValues } from './components';
import { ArchiveModal, archiveModalCreateSchema, AssignModal, assignModalCreateSchema } from './components';
import { useColumns } from './useColumns';

export const Unassigned: FC = () => {
  const [t] = useTranslation(['backend', 'common', 'serviceDashboard', 'adTags']);
  const { sortProps, sortParameters } = useSortAndOrderState<ColumnName>('createdDate', OrderDirection.ASC);
  const { pagination, tasks, tasksLoading, refetchTasks, info, selection } = useTasks({
    status: TaskTableStatus.UNASSIGNED,
    sortParameters,
  });

  const assignModalForm = useForm<AssignModalFormValues>({
    values: {
      assignee: null,
    },
    resolver: useResolver(assignModalCreateSchema(t)),
  });
  const {
    formState: { isSubmitting: isAssignModalSubmitting },
    reset: resetAssignForm,
  } = assignModalForm;

  const archiveModalForm = useForm<ArchiveModalFormValues>({
    values: {
      outcome: null,
    },
    resolver: useResolver(archiveModalCreateSchema(t)),
  });
  const {
    formState: { isSubmitting: isArchiveModalSubmitting },
    reset: resetArchiveForm,
  } = archiveModalForm;

  const toggleSelectAll = useMemo<SelectAllProps>(
    () => ({
      checked: selection.isSelectedAll,
      onChange: selection.toggleSelectAll,
      disabled: isAssignModalSubmitting || isArchiveModalSubmitting,
      indeterminate: selection.indeterminate,
    }),
    [isArchiveModalSubmitting, isAssignModalSubmitting, selection.indeterminate, selection.isSelectedAll, selection.toggleSelectAll],
  );
  const columns = useColumns(t, toggleSelectAll);

  const assignModal = useModalState<IdTaskDto[]>({
    onAfterClose: resetAssignForm,
  });
  const archiveModal = useModalState<IdTaskDto[]>({
    onAfterClose: resetArchiveForm,
  });

  const handleBulkAssign = useCallbackRef(() => {
    assignModal.open(selection.selectedIds);
  });

  const handleBulkArchive = useCallbackRef(() => {
    archiveModal.open(selection.selectedIds);
  });

  return (
    <>
      {assignModal.isMounted && <AssignModal state={assignModal} form={assignModalForm} selection={selection} reloadTasks={refetchTasks} />}
      {archiveModal.isMounted && (
        <ArchiveModal state={archiveModal} form={archiveModalForm} selection={selection} reloadTasks={refetchTasks} />
      )}
      <Container fluid>
        <Table.Wrapper data-testid='tasks-table-unassigned'>
          <TableTabs />
          <TableFilters
            info={info}
            pagination={pagination}
            sortProps={sortProps}
            columns={columns}
            bulkActions={
              <>
                <div>{t('common:AMOUNT_SELECTED', { amount: selection.selectedIds.length })}</div>
                <Button secondary onClick={handleBulkAssign}>
                  <Icons.PersonAdd />
                  {t('common:ASSIGN')}
                </Button>
                <DropdownList
                  renderButton={(isShown) => (
                    <Button iconOnly secondary>
                      <Icons.More active={isShown} />
                    </Button>
                  )}>
                  <DropdownList.DropdownItem icon={<Icons.Archived />} onClick={handleBulkArchive}>
                    {t('adTags:ARCHIVE_TAG')}
                  </DropdownList.DropdownItem>
                </DropdownList>
              </>
            }
            showBulkActions={selection.isSomethingSelected}
            isLoading={tasksLoading}
          />
          <Table>
            {(tasksLoading || tasks.length > 0) && <TableHeader getSortProps={sortProps} columns={columns} />}
            {!tasksLoading &&
              tasks.map((task) => (
                <TaskRow
                  key={task.id?.toString()}
                  item={task}
                  columns={columns}
                  selection={selection}
                  selectionDisabled={isAssignModalSubmitting || isArchiveModalSubmitting}
                  assignModalState={assignModal}
                  archiveModalState={archiveModal}
                />
              ))}
            {tasksLoading && <Table.Loader rows={pagination.perPage} columnsLength={columns.length} />}
          </Table>
          {!tasksLoading && tasks.length === 0 && <Table.NoData message={t('common:THERE_ARE_NO_ENTRIES')} />}
          <Table.Pagination info={info} pagination={pagination} isLoading={tasksLoading} />
        </Table.Wrapper>
      </Container>
    </>
  );
};
